// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import {
  CheckCircleOutlineRounded,
  DoneAllRounded,
  InboxOutlined,
  NotificationsOutlined,
} from '@/constants/hardfin-standard-icons';
import { useClickToConfirm } from '@/hooks/use-click-to-confirm';
import { useDisclosure } from '@/hooks/use-disclosure';
import { KnownInboxEntry } from '@/notification/api/notification-types';
import {
  useInbox,
  useInboxReadState,
  useUnreadCount,
} from '@/notification/hooks/use-inbox';
import { toRenderedText } from '@/notification/utilities/render-message';
import { toRelativeTime } from '@/utilities/date-time';
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link } from '@tanstack/react-router';
import { Fragment, UIEvent, useRef, useState } from 'react';

const INITIAL_LIMIT = 6;
const LOAD_MORE_PAGE_SIZE = 6;
// The inbox route refuses a larger page than this
const MAX_LIMIT = 100;
const LOAD_MORE_THRESHOLD_PX = 48;
const LIST_MAX_HEIGHT_PX = 360;

export function NotificationBell() {
  const popover = useDisclosure();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [limit, setLimit] = useState(INITIAL_LIMIT);
  const unread = useUnreadCount();
  const { markRead, markAllRead } = useInboxReadState();
  // The popover is the only thing that reads messages, so nothing is fetched
  // until it opens
  // The popover draws titles, so it leaves the body templates on the server
  const inbox = useInbox({
    state: 'unread',
    page: 1,
    limit,
    isSparse: true,
    enabled: popover.isOpen,
  });

  const messages = inbox.data?.results ?? [];
  // What the filter matched, which is not what the popover drew: a message this
  // bundle cannot draw is still one the caller has unread
  const totalCount = inbox.data?.totalCount ?? 0;
  const isSaving = markRead.isPending || markAllRead.isPending;

  // Clearing every unread message cannot be undone in one action, and the
  // popover puts this button beside the per-message tick, so it confirms the
  // same way the inbox page does
  const markAllConfirm = useClickToConfirm(() => {
    markAllRead.mutate();
  });

  const handleOpen = () => {
    setLimit(INITIAL_LIMIT);
    markAllConfirm.reset();
    popover.open();
  };

  const handleListScroll = (event: UIEvent<HTMLUListElement>) => {
    const list = event.currentTarget;
    const isNearBottom =
      list.scrollTop + list.clientHeight >=
      list.scrollHeight - LOAD_MORE_THRESHOLD_PX;
    const hasMore = limit < totalCount;
    if (isNearBottom && hasMore && !inbox.isFetching) {
      setLimit((current) => Math.min(current + LOAD_MORE_PAGE_SIZE, MAX_LIMIT));
    }
  };

  return (
    <>
      <Tooltip arrow title="Notifications">
        <IconButton
          ref={anchorRef}
          onClick={handleOpen}
          aria-label="Notifications"
          sx={{ marginRight: 0.5, padding: 0.5 }}
        >
          {/* A count nobody has answered for yet is not a count of zero, so
              the bubble waits for the answer rather than drawing one */}
          <Badge
            badgeContent={unread.data}
            color="error"
            invisible={!unread.isSuccess || unread.data === 0}
          >
            <NotificationsOutlined />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={popover.isOpen}
        anchorEl={anchorRef.current}
        onClose={popover.close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: (theme) => ({
              marginTop: 1,
              maxWidth: `calc(100vw - ${theme.spacing(4)})`,
              width: 400,
            }),
          },
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          paddingX={2}
          paddingY={1}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h6">Notifications</Typography>
            {unread.isSuccess && (
              <Typography variant="body2" color="text.secondary">
                {unread.data} unread
              </Typography>
            )}
          </Stack>
          <Button
            size="small"
            variant={markAllConfirm.isPending ? 'contained' : 'text'}
            startIcon={<DoneAllRounded />}
            disabled={totalCount === 0 || isSaving}
            onClick={markAllConfirm.confirm}
          >
            {markAllConfirm.isPending ? 'Click to confirm' : 'Mark all read'}
          </Button>
        </Stack>
        <Divider />
        <NotificationBellBody
          isLoading={inbox.isPending}
          isError={inbox.isError}
          messages={messages}
          droppedMessageCount={inbox.data?.droppedMessageCount ?? 0}
          isSaving={isSaving}
          onScroll={handleListScroll}
          onMarkRead={(messageKey) => markRead.mutate([messageKey])}
        />
        <Divider />
        <Button
          component={Link}
          to="/notifications"
          fullWidth
          onClick={popover.close}
          sx={{ borderRadius: 0, paddingY: 2 }}
        >
          View all notifications
        </Button>
      </Popover>
    </>
  );
}

interface NotificationBellBodyProps {
  isLoading: boolean;
  isError: boolean;
  messages: KnownInboxEntry[];
  droppedMessageCount: number;
  isSaving: boolean;
  onScroll: (event: UIEvent<HTMLUListElement>) => void;
  onMarkRead: (messageKey: string) => void;
}

function NotificationBellBody(props: NotificationBellBodyProps) {
  if (props.isLoading) {
    return (
      <Stack alignItems="center" padding={4}>
        <CircularProgress size={24} />
      </Stack>
    );
  }
  if (props.isError) {
    return (
      <Stack alignItems="center" padding={4}>
        <Typography variant="body2" color="text.secondary">
          Notifications could not be loaded
        </Typography>
      </Stack>
    );
  }
  if (props.messages.length === 0) {
    if (props.droppedMessageCount > 0) {
      return (
        <Stack alignItems="center" spacing={1} padding={4}>
          <InboxOutlined color="disabled" fontSize="large" />
          <Typography variant="subtitle2">
            These notifications could not be displayed
          </Typography>
        </Stack>
      );
    }
    return (
      <Stack alignItems="center" spacing={1} padding={4}>
        <CheckCircleOutlineRounded color="success" fontSize="large" />
        <Typography variant="subtitle2">You&apos;re all caught up</Typography>
      </Stack>
    );
  }

  return (
    <List
      disablePadding
      onScroll={props.onScroll}
      sx={{ maxHeight: LIST_MAX_HEIGHT_PX, overflowY: 'auto' }}
    >
      {props.messages.map((message, index) => (
        <Fragment key={message.key}>
          {index > 0 && <Divider component="li" />}
          <ListItem
            sx={{ alignItems: 'flex-start', gap: 1.25, paddingY: 1.25 }}
          >
            {/* Every message the popover lists is unread, so the dot marks
                each one rather than counting anything */}
            <Box
              aria-label="Unread"
              sx={{
                backgroundColor: 'primary.main',
                borderRadius: '50%',
                flexShrink: 0,
                height: 8,
                marginTop: 1.25,
                width: 8,
              }}
            />
            <Stack minWidth={0} flex={1} spacing={0.25}>
              <Typography variant="body2" fontWeight={700}>
                {toRenderedText(message.titleTemplate, message.context)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {toRelativeTime(message.createdAt)}
              </Typography>
            </Stack>
            <Button
              size="small"
              variant="text"
              disabled={props.isSaving}
              sx={{ alignSelf: 'center', flexShrink: 0 }}
              onClick={() => props.onMarkRead(message.key)}
            >
              Mark as read
            </Button>
          </ListItem>
        </Fragment>
      ))}
    </List>
  );
}
